<template>
  <div
    class="w-full flex flex-col"
    :style="{ maxHeight: `${viewportHeight - 75}px` }"
  >
    <!-- Handle -->
    <div
      class="w-[60px] min-h-1 max-h-1 bg-gray-800/20 rounded-xs self-center mt-2.5 mb-1.5"
    />

    <!-- File details -->
    <div
      class="w-full px-[14px] pt-[5.5px] pb-[3.5px] font-secondary text-gray-800"
    >
      <div class="flex items-center">
        <ThumbnailIcon
          :image-url="thumbnailUrl"
          :icon-size="36"
          :type="file.type"
          class="min-w-9 max-w-9 h-9 mr-2.5"
        />
        <div v-truncate class="w-full">
          <div
            class="font-medium text-[16px] leading-[18px] mb-[1px]"
            data-truncate-target
          >
            {{ file.name }}
          </div>
          <div class="flex items-center text-[13px]">
            <template v-if="isInactive">- -</template>
            <template v-else-if="firstTimestamp">
              <Icon :size="15" name="Dacr" class="fill-gray-800/50 mr-1" />
              <div class="opacity-50">
                {{ formatTimestamp(firstTimestamp) }}
              </div>
              <div
                v-if="hasMoreRegistrations"
                class="flex items-center font-medium text-gray-500 cursor-pointer gap-x-1.5 ml-2"
                @click="handleTimestampClick"
              >
                <div>+{{ remainingRegistrations }} more</div>
                <Icon
                  name="ChevronUp"
                  :size="14"
                  class="rotate-180 stroke-gray-500"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
      <div
        v-if="!isInactive && certificateUrl"
        class="text-[16px] mt-[5px] ml-[47px] text-green-700 cursor-pointer"
        @click="handleCertificateClick"
      >
        View Dacr Certificate
      </div>
    </div>

    <div
      class="w-full mb-0! my-[12.5px] min-h-[1px] max-h-[1px] bg-gray-800/10"
    />

    <!-- Actions -->
    <div class="overflow-y-auto pt-[12.5px] pb-[12.5px]">
      <TallListItem
        icon="Share"
        :disabled="isInactive"
        @click="handleAction(Dialogs.Share)"
      >
        Share
      </TallListItem>

      <TallListItem
        icon="Send"
        :disabled="isInactive"
        @click="handleAction(Dialogs.FileAccess)"
      >
        Invite
      </TallListItem>

      <TallListItem
        icon="Download"
        :disabled="!canDownload"
        @click="handleAction(Dialogs.Download)"
      >
        Download
      </TallListItem>

      <div class="w-full my-[12.5px] min-h-[1px] max-h-[1px] bg-gray-800/10" />

      <TallListItem icon="Settings" @click="handleAction(Dialogs.FileSettings)">
        Copyright settings
      </TallListItem>

      <TallListItem
        icon="Lock"
        @click="handleAction(Dialogs.FilePrivacySettings)"
      >
        Privacy controls
      </TallListItem>

      <TallListItem
        icon="Move"
        @click="handleAction(Dialogs.MoveItems, { ids: [file.id] })"
      >
        Move
      </TallListItem>

      <TallListItem
        class="text-red-600"
        @click="handleAction(Dialogs.ArchiveFiles, { files: [file] })"
      >
        <Icon name="Archive" :size="20" class="mr-5 stroke-red-600" />
        Archive
      </TallListItem>

      <div class="w-full my-[12.5px] min-h-[1px] max-h-[1px] bg-gray-800/10" />

      <TallListItem icon="Info" @click="handleFileInfoClick">
        Copyright info
      </TallListItem>

      <TallListItem
        icon="Clock"
        :disabled="isInactive"
        @click="handleTransactionClick"
      >
        Transaction history
      </TallListItem>

      <div class="w-full my-[12.5px] min-h-[1px] max-h-[1px] bg-gray-800/10" />

      <TallListItem icon="Coin" disabled>
        Sell/Transfer
        <template #right>
          <Pill>Coming soon</Pill>
        </template>
      </TallListItem>

      <TallListItem icon="Coin" disabled>
        License
        <template #right>
          <Pill>Coming soon</Pill>
        </template>
      </TallListItem>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Icon, Pill, ThumbnailIcon } from '@dacr/ui'
import { TallListItem } from '@/shared/components/Menu'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { useDevice } from '@/shared/composables/useDevice'
import {
  CopyrightStatus,
  FilePermission,
  type File,
} from '@/shared/graphql/router/graphql'
import { formatTimestamp } from '@/shared/utils/datetime'
import { applyAuthorizationToken } from '@/shared/utils/url'

const { open } = useDialog()
const { viewportHeight } = useDevice()

const props = defineProps<{
  file: File
}>()

const file = computed(() => props.file)

const isInactive = computed(
  () => file.value.copyright?.status === CopyrightStatus.Inactive
)

const canDownload = computed(() =>
  file.value.permissions.includes(FilePermission.Download)
)

const certificateUrl = computed(
  () => file.value.copyright?.registrations?.[0]?.certificateUrl
)

const firstTimestamp = computed(
  () => file.value.copyright?.registrations?.[0]?.timestamp
)

const registrations = computed(() => file.value.copyright?.registrations ?? [])

const hasMoreRegistrations = computed(() => registrations.value.length > 1)

const remainingRegistrations = computed(() => registrations.value.length - 1)

const thumbnailUrl = computed(() =>
  applyAuthorizationToken(file.value.asset?.previewImageUrl)
)

const handleCertificateClick = () => {
  if (certificateUrl.value) window.open(certificateUrl.value, '_blank')
}

const handleAction = (
  dialog: Dialogs,
  extraProps?: Record<string, unknown>
) => {
  open(dialog, {
    props: { file: file.value, ...extraProps },
  })
}

const handleTimestampClick = () => {
  open(Dialogs.Registrations, {
    props: { file: file.value },
  })
}

const handleFileInfoClick = () => {
  open(Dialogs.CopyrightInfo, {
    props: { file: file.value },
  })
}

const handleTransactionClick = () => {
  open(Dialogs.TransactionHistory, {
    props: { transactions: [] },
  })
}
</script>
